import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Flex, Grid, Text } from '@backstage/ui';
import type {
  CheckFilter,
  TrackDefinition,
} from '@internal/backstage-plugin-pulse-common';
import {
  useCreateTrack,
  usePulseChecks,
  useTeamOwnerFilter,
  useUpdateTrack,
} from '../../../../hooks';
import { computeTrackFieldErrors, slugifyTrackId } from '../lib';
import { TrackDetailsSection } from './track-details-section';
import { TrackChecksSection } from './track-checks-section';
import { TrackFiltersSection } from './track-filters-section';
import { TrackPreview } from './track-preview';

type TrackFormProps = {
  initial?: TrackDefinition;
  isReadOnly?: boolean;
  onDone: () => void;
  onCreated: (trackId: string) => void;
  onUpdated?: (trackId: string) => void;
};

export const TrackForm = ({
  initial,
  isReadOnly = false,
  onDone,
  onCreated,
  onUpdated,
}: TrackFormProps) => {
  const isEdit = Boolean(initial);
  const { value: checks, loading: checksLoading } = usePulseChecks();
  const { create, loading: creating } = useCreateTrack();
  const { update, loading: updating } = useUpdateTrack();
  const { primaryOwnedGroupRef } = useTeamOwnerFilter();
  const [name, setName] = useState(initial?.name ?? '');
  const [id, setId] = useState(initial?.id ?? '');
  // Once the user edits the id, stop auto-filling it from the name.
  const [idEdited, setIdEdited] = useState(false);
  const [description, setDescription] = useState(initial?.description ?? '');
  const [ownerEntityRef, setOwnerEntityRef] = useState(
    initial?.ownerEntityRef ?? '',
  );
  const [documentationUrl, setDocumentationUrl] = useState(
    initial?.documentationUrl ?? '',
  );
  const [inactive, setInactive] = useState(initial?.inactive ?? false);
  const [mandatoryCheckIds, setMandatoryCheckIds] = useState<string[]>(
    initial?.levels
      .filter(level => level.importance === 'mandatory')
      .flatMap(level => level.checkIds) ?? [],
  );
  const [mandatoryLevelDescription, setMandatoryLevelDescription] = useState(
    initial?.levels.find(level => level.importance === 'mandatory')
      ?.description ?? '',
  );
  const [recommendedCheckIds, setRecommendedCheckIds] = useState<string[]>(
    initial?.levels
      .filter(level => level.importance === 'recommended')
      .flatMap(level => level.checkIds) ?? [],
  );
  const [recommendedLevelDescription, setRecommendedLevelDescription] =
    useState(
      initial?.levels.find(level => level.importance === 'recommended')
        ?.description ?? '',
    );
  const [includeConditions, setIncludeConditions] = useState<CheckFilter[]>(
    initial?.filter ?? [],
  );
  const [excludeConditions, setExcludeConditions] = useState<CheckFilter[]>(
    initial?.exclude ?? [],
  );
  const [expandedCheckId, setExpandedCheckId] = useState('');
  // Errors stay hidden until the user first attempts to create the track.
  const [submitted, setSubmitted] = useState(false);
  // Bumped on each attempt so pickers know to validate rows added since.
  const [validationAttempt, setValidationAttempt] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const ownerDefaultApplied = useRef(false);

  useEffect(() => {
    if (ownerDefaultApplied.current) {
      return;
    }
    if (ownerEntityRef) {
      ownerDefaultApplied.current = true;
      return;
    }
    if (primaryOwnedGroupRef) {
      setOwnerEntityRef(primaryOwnedGroupRef);
      ownerDefaultApplied.current = true;
    }
  }, [primaryOwnedGroupRef, ownerEntityRef]);

  const toggleExpandedCheck = (checkId: string) => {
    setExpandedCheckId(current => (current === checkId ? '' : checkId));
  };

  const supportsTwoLevelEditShape = useMemo(() => {
    if (!initial) {
      return true;
    }

    const levels = initial.levels ?? [];
    if (levels.length === 0) {
      return true;
    }

    const mandatoryLevels = levels.filter(
      level => level.importance === 'mandatory',
    );
    const recommendedLevels = levels.filter(
      level => level.importance === 'recommended',
    );

    return (
      levels.length <= 2 &&
      mandatoryLevels.length <= 1 &&
      recommendedLevels.length <= 1 &&
      levels.every(level => [1, 2].includes(level.ordinal))
    );
  }, [initial]);

  const cannotEditUnsupportedLevelShape = isEdit && !supportsTwoLevelEditShape;

  const trackId = id.trim();

  const {
    idFormatError,
    idFieldError,
    nameError,
    ownerError,
    documentationUrlError,
    mandatoryChecksError,
    hasInvalidMandatoryRows,
    hasInvalidRecommendedRows,
    duplicateChecksError,
    excludeWithoutIncludeError,
    isValid,
  } = computeTrackFieldErrors({
    submitted,
    id,
    name,
    ownerEntityRef,
    documentationUrl,
    mandatoryCheckIds,
    recommendedCheckIds,
    includeConditions,
    excludeConditions,
  });

  const track = useMemo<TrackDefinition>(() => {
    const mandatoryCheckIdsForTrack = mandatoryCheckIds.filter(Boolean);
    const recommendedCheckIdsForTrack = recommendedCheckIds.filter(Boolean);
    const mandatoryDescription = mandatoryLevelDescription.trim();
    const recommendedDescription = recommendedLevelDescription.trim();

    return {
      id: trackId,
      name: name.trim(),
      ownerEntityRef: ownerEntityRef.trim(),
      ...(description.trim() ? { description: description.trim() } : {}),
      ...(documentationUrl.trim()
        ? { documentationUrl: documentationUrl.trim() }
        : {}),
      ...(inactive ? { inactive: true } : {}),
      ...(includeConditions.length ? { filter: includeConditions } : {}),
      ...(excludeConditions.length ? { exclude: excludeConditions } : {}),
      levels: [
        {
          ordinal: 1,
          importance: 'mandatory',
          checkIds: mandatoryCheckIdsForTrack,
          ...(mandatoryDescription
            ? { description: mandatoryDescription }
            : {}),
        },
        ...(recommendedCheckIdsForTrack.length || recommendedDescription
          ? [
              {
                ordinal: 2,
                importance: 'recommended' as const,
                checkIds: recommendedCheckIdsForTrack,
                ...(recommendedDescription
                  ? { description: recommendedDescription }
                  : {}),
              },
            ]
          : []),
      ],
    };
  }, [
    description,
    documentationUrl,
    excludeConditions,
    inactive,
    includeConditions,
    mandatoryCheckIds,
    mandatoryLevelDescription,
    name,
    ownerEntityRef,
    recommendedCheckIds,
    recommendedLevelDescription,
    trackId,
  ]);

  const handleSave = async () => {
    if (isReadOnly || cannotEditUnsupportedLevelShape) {
      return;
    }
    setError(null);
    setSubmitted(true);
    setValidationAttempt(current => current + 1);
    if (!isValid) {
      setError('Please fix the highlighted fields.');
      return;
    }

    try {
      if (isEdit) {
        const immutableId = initial!.id;
        await update(immutableId, { ...track, id: immutableId });
        onUpdated?.(immutableId);
      } else {
        const created = await create(track);
        onCreated(created.id);
      }
    } catch (saveError) {
      setError((saveError as Error).message);
    }
  };

  const saving = creating || updating;
  const readOnlyError = ((): string | undefined => {
    if (isReadOnly)
      return 'This track is defined in YAML and cannot be edited here.';
    if (cannotEditUnsupportedLevelShape)
      return 'This track uses an unsupported level structure and cannot be edited here.';
    return undefined;
  })();

  return (
    <Grid.Root columns={{ initial: '1', lg: '2' }} gap="6">
      <Grid.Item colSpan={{ initial: '1', lg: '1' }}>
        <Flex direction="column" gap="4" pb="var(--bui-space-8)">
          <TrackDetailsSection
            name={name}
            onNameChange={value => {
              setName(value);
              if (!idEdited && !isEdit) {
                setId(slugifyTrackId(value));
              }
            }}
            id={id}
            onIdChange={value => {
              setIdEdited(true);
              setId(value);
            }}
            description={description}
            onDescriptionChange={setDescription}
            ownerEntityRef={ownerEntityRef}
            onOwnerEntityRefChange={setOwnerEntityRef}
            documentationUrl={documentationUrl}
            onDocumentationUrlChange={setDocumentationUrl}
            inactive={inactive}
            onInactiveChange={setInactive}
            isReadOnly={isReadOnly || cannotEditUnsupportedLevelShape}
            nameError={nameError}
            idFormatError={idFormatError}
            idFieldError={idFieldError}
            ownerError={ownerError}
            documentationUrlError={documentationUrlError}
          />

          <TrackChecksSection
            checks={checks ?? []}
            mandatoryCheckIds={mandatoryCheckIds}
            onMandatoryCheckIdsChange={setMandatoryCheckIds}
            mandatoryLevelDescription={mandatoryLevelDescription}
            onMandatoryLevelDescriptionChange={setMandatoryLevelDescription}
            recommendedCheckIds={recommendedCheckIds}
            onRecommendedCheckIdsChange={setRecommendedCheckIds}
            recommendedLevelDescription={recommendedLevelDescription}
            onRecommendedLevelDescriptionChange={setRecommendedLevelDescription}
            onCheckExpanded={toggleExpandedCheck}
            expandedCheckId={expandedCheckId}
            validationAttempt={validationAttempt}
            isLoading={checksLoading}
            isReadOnly={isReadOnly || cannotEditUnsupportedLevelShape}
            mandatoryChecksError={mandatoryChecksError}
            hasInvalidMandatoryRows={hasInvalidMandatoryRows}
            hasInvalidRecommendedRows={hasInvalidRecommendedRows}
            duplicateChecksError={duplicateChecksError}
          />

          <TrackFiltersSection
            includeConditions={includeConditions}
            onIncludeConditionsChange={setIncludeConditions}
            excludeConditions={excludeConditions}
            onExcludeConditionsChange={setExcludeConditions}
            excludeWithoutIncludeError={excludeWithoutIncludeError}
            isReadOnly={isReadOnly || cannotEditUnsupportedLevelShape}
          />
        </Flex>
      </Grid.Item>

      <Grid.Item colSpan={{ initial: '1', lg: '1' }}>
        <Flex direction="column" gap="6">
          <TrackPreview track={track} />
        </Flex>
      </Grid.Item>

      <Grid.Item colSpan={{ initial: '1', lg: '2' }}>
        <Flex direction="row" justify="end" align="center" gap="2">
          <Flex align="center" gap="4">
            {error ?? readOnlyError ? (
              <Text variant="body-small" color="danger">
                {error ?? readOnlyError}
              </Text>
            ) : null}
          </Flex>
          <Button variant="secondary" isDisabled={saving} onPress={onDone}>
            Cancel
          </Button>
          <Button
            variant="primary"
            isPending={saving}
            isDisabled={saving || isReadOnly || cannotEditUnsupportedLevelShape}
            onPress={handleSave}
          >
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </Flex>
      </Grid.Item>
    </Grid.Root>
  );
};
